import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api.ts'

interface Hit {
  kind: 'klient' | 'maszyna' | 'oferta' | 'zamowienie' | 'zamowienie_producent' | 'egzemplarz' | 'serwis'
  id: string
  title: string
  subtitle: string
  url: string
}

const KIND_STYLE: Record<Hit['kind'], string> = {
  klient: 'bg-blue-100 text-blue-700',
  maszyna: 'bg-green-100 text-green-700',
  oferta: 'bg-brand-100 text-brand-700',
  zamowienie: 'bg-purple-100 text-purple-700',
  zamowienie_producent: 'bg-amber-100 text-amber-700',
  egzemplarz: 'bg-teal-100 text-teal-700',
  serwis: 'bg-rose-100 text-rose-700',
}

const KIND_LABEL: Record<Hit['kind'], string> = {
  klient: 'klient',
  maszyna: 'maszyna',
  oferta: 'oferta',
  zamowienie: 'zamówienie',
  zamowienie_producent: 'u producenta',
  egzemplarz: 'egzemplarz',
  serwis: 'serwis',
}

/**
 * Ctrl+K from anywhere (SPEC 5.7). Keyboard-first: type, arrow, Enter — the
 * salesperson in an orchard shouldn't have to aim at anything with a mouse.
 * Serial numbers and offer numbers join the results as their stages land.
 */
export function GlobalSearch() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[]>([])
  const [active, setActive] = useState(0)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Ctrl+K / Cmd+K opens, Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (open) {
      setQ('')
      setHits([])
      setActive(0)
      inputRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open || q.trim().length < 2) {
      setHits([])
      return
    }
    const t = setTimeout(() => {
      setLoading(true)
      // Offline: search the local snapshot (clients + catalogue) instead of the API.
      const run = navigator.onLine
        ? api.get<{ hits: Hit[] }>(`/api/search?q=${encodeURIComponent(q.trim())}`).then((r) => r.hits)
        : import('../offline/cache.ts').then((m) => m.searchOffline(q.trim()) as Promise<Hit[]>)
      run
        .then((hits) => {
          setHits(hits)
          setActive(0)
        })
        .catch(() => setHits([]))
        .finally(() => setLoading(false))
    }, 120)
    return () => clearTimeout(t)
  }, [q, open])

  if (!open) return null

  function go(hit: Hit) {
    setOpen(false)
    nav(hit.url)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && hits[active]) {
      e.preventDefault()
      go(hits[active])
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/30 flex items-start justify-center pt-24"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Szukaj: klient, maszyna, numer oferty…"
          className="w-full px-4 py-3 text-sm border-0 border-b border-slate-200 focus:outline-none"
        />

        {q.trim().length >= 2 && (
          <div className="max-h-80 overflow-y-auto">
            {hits.map((h, i) => (
              <button
                key={h.kind + h.id}
                onClick={() => go(h)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-4 py-2 flex items-center gap-3 ${
                  i === active ? 'bg-brand-50' : ''
                }`}
              >
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded shrink-0 ${KIND_STYLE[h.kind]}`}>
                  {KIND_LABEL[h.kind]}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800 truncate">{h.title}</span>
                  <span className="block text-xs text-slate-400 truncate">{h.subtitle}</span>
                </span>
              </button>
            ))}
            {!loading && hits.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">Nic nie znaleziono.</div>
            )}
          </div>
        )}

        <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-400 flex gap-3">
          <span>↑↓ wybór</span>
          <span>Enter otwórz</span>
          <span>Esc zamknij</span>
        </div>
      </div>
    </div>
  )
}
